import React, { Component, type ChangeEvent, type ReactNode } from 'react';
import { Field, ErrorMessage, HelperMessage } from '@atlaskit/form';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Link from '@atlaskit/link';
import { Box, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { PROJECT_NAME_MIN_LENGTH, PROJECT_URL_MAX_LENGTH } from '../../../model/constants';
import type { ValidationMessage } from '../../../state/ui/types';
import messages from '../messages';

export type BaseProps = {
	label: string | ReactNode;
	message: ValidationMessage;
	value: string;
	name: string;
	isDisabled?: boolean;
	isRequired?: boolean;
	focusOnField: boolean;
	onBlur: (() => void) | undefined;
	onChange: (arg1: ChangeEvent<HTMLInputElement>) => void;
};

export type Props = BaseProps & {
	projectNameMaxLength: number | undefined;
	projectKeyMaxLength: number | undefined;
};

export type PropsWithIntl = Props & {
	intl: IntlShape | undefined;
};

// eslint-disable-next-line jira/react/no-class-components
export class FormInput extends Component<PropsWithIntl> {
	static defaultProps = {
		message: undefined,
	};

	componentDidUpdate(prevProps: PropsWithIntl) {
		const { props, inputRef } = this;
		if (inputRef && props.focusOnField && !prevProps.focusOnField) {
			inputRef.focus();
		}
	}

	inputRef: HTMLInputElement | null | undefined;

	onInputRef = (ref?: HTMLInputElement | null) => {
		this.inputRef = ref;
	};

	onInputBlur = () => {
		if (this.props.onBlur) {
			this.props.onBlur();
		}
	};

	render() {
		const {
			label,
			name,
			isDisabled,
			isRequired,
			message,
			value,
			onChange,
			intl,
			projectKeyMaxLength,
			projectNameMaxLength,
		} = this.props;
		const isInvalid = message && (message.type === 'warning' || message.type === 'error');
		const learnMoreAboutProjectKeysLink =
			'https://support.atlassian.com/jira-cloud-administration/docs/edit-a-projects-details/';
		let invalidMessage: string | undefined;

		if (isInvalid === true && message) {
			// @ts-expect-error - TS2339 - Property 'key' does not exist on type 'ValidationMessageFromClient | ValidationMessageFromServer'.
			if (message.key !== undefined && intl) {
				// I would love to use constants here but flow cannot cast to empty when using a union type with
				// constants. Flow will tell us if our string doesn't match the constant so we're still protected.
				// @ts-expect-error - TS2339 - Property 'key' does not exist on type 'ValidationMessageFromClient | ValidationMessageFromServer'.
				switch (message.key) {
					case 'TOO_SHORT_PROJECT_NAME_ERROR':
						invalidMessage = intl.formatMessage(messages.tooShortProjectNameError, {
							minCharacters: PROJECT_NAME_MIN_LENGTH,
						});
						break;
					case 'PROJECT_KEY_CHANGE_WARNING':
						invalidMessage = intl.formatMessage(messages.projectKeyChangeWarning, {
							a: (chunks: string) => (
								<Link
									href={learnMoreAboutProjectKeysLink}
									rel="noopener noreferrer nofollow"
									target="_blank"
								>
									{chunks}
								</Link>
							),
						});
						break;
					case 'TOO_LONG_PROJECT_NAME_ERROR':
						// This should NEVER happen as we only set this error when projectNameMaxLength is present.
						// Nevertheless we'll include this message as a fallback
						if (projectNameMaxLength === undefined) {
							invalidMessage = intl.formatMessage(messages.tooLongProjectNameUnknownLimitError);
						} else {
							invalidMessage = intl.formatMessage(messages.tooLongProjectNameError, {
								maxCharacters: projectNameMaxLength,
							});
						}
						break;
					case 'REQUIRED_PROJECT_KEY_ERROR':
						invalidMessage = intl.formatMessage(messages.requiredProjectKeyError);
						break;
					case 'TOO_LONG_PROJECT_KEY_ERROR':
						// This should NEVER happen as we only set this error when projectKeyMaxLength is present.
						// Nevertheless we'll include this message as a fallback
						if (projectKeyMaxLength === undefined) {
							invalidMessage = intl.formatMessage(messages.tooLongProjectKeyUnknownLimitError);
						} else {
							invalidMessage = intl.formatMessage(messages.tooLongProjectKeyError, {
								maxCharacters: projectKeyMaxLength,
							});
						}
						break;
					case 'TOO_LONG_PROJECT_URL_ERROR':
						invalidMessage = intl.formatMessage(messages.tooLongProjectUrlError, {
							maxCharacters: PROJECT_URL_MAX_LENGTH,
						});
						break;
					case 'INVALID_URL_PATTERN':
						invalidMessage = intl.formatMessage(messages.invalidPatternProjectUrl);
						break;
					case 'INVALID_PATTERN_PROJECT_KEY_ERROR':
						invalidMessage = intl.formatMessage(messages.invalidPatternProjectKeyError);
						break;
					default:
						// prettier-ignore
						// @ts-expect-error - TS2339 - Property 'key' does not exist on type 'ValidationMessageFromClient | ValidationMessageFromServer'.
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						(message.key as never);
						invalidMessage = undefined;
				}
				// @ts-expect-error - TS2339 - Property 'body' does not exist on type 'ValidationMessageFromClient | ValidationMessageFromServer'.
			} else if (message.body !== undefined) {
				// @ts-expect-error - TS2339 - Property 'body' does not exist on type 'ValidationMessageFromClient | ValidationMessageFromServer'.
				invalidMessage = message.body;
			}
		}
		const WarningMessageConditioned = HelperMessage;
		const fieldWithErrorAndWarning = (
			<Field isRequired={isRequired} name={name} label={label}>
				{({ fieldProps }) => (
					<>
						<Textfield
							{...fieldProps}
							value={value}
							onChange={onChange}
							isInvalid={message?.type === 'error'}
							aria-invalid={message?.type === 'error'}
							ref={this.onInputRef}
							onBlur={this.onInputBlur}
							isDisabled={isDisabled}
							{...(message?.type === 'error'
								? { 'aria-describedby': `${fieldProps.id}-error` }
								: undefined)}
						/>
						{invalidMessage !== undefined && message?.type === 'error' && (
							<ErrorMessage>{invalidMessage}</ErrorMessage>
						)}
						{invalidMessage !== undefined && message?.type === 'warning' && (
							<WarningMessageConditioned>
								<>
									<Box as="span" xcss={WarningIconWrapperNew}>
										<WarningIcon label={invalidMessage} size="small" />
									</Box>
									<Box as="span" xcss={WarningMessageWrapperNew}>
										{invalidMessage}
									</Box>
								</>
							</WarningMessageConditioned>
						)}
					</>
				)}
			</Field>
		);

		return fieldWithErrorAndWarning;
	}
}

export default injectIntl(FormInput);

const WarningIconWrapperNew = xcss({
	color: 'color.text.warning',
	verticalAlign: 'middle',
});

const WarningMessageWrapperNew = xcss({
	color: 'color.text.warning',
	fontSize: 'font.body.small',
});
